import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

// Async thunk for toggling auto-import
export const toggle_auto_import = createAsyncThunk(
  "autoImport/toggle_auto_import",
  async ({ enabled }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post("/dashboard/auto-import/toggle", {
        enabled
      }, {
        withCredentials: true,
      });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk for getting auto-import status
export const get_auto_import_status = createAsyncThunk(
  "autoImport/get_auto_import_status",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get("/dashboard/auto-import/status", {
        withCredentials: true,
      });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk for updating cron schedule
export const update_cron_schedule = createAsyncThunk(
  "autoImport/update_cron_schedule",
  async ({ hour, minute, timezone }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.put("/dashboard/auto-import/cron-schedule", {
        hour,
        minute,
        timezone
      }, {
        withCredentials: true,
      });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const autoImportSlice = createSlice({
  name: "autoImport",
  initialState: {
    autoImportEnabled: false,
    lastAutoImport: null,
    hasVoucherNumber: false,
    hasPosConfig: false,
    voucherNumber: null,
    sellerName: null,
    cronSchedule: {
      hour: 12,
      minute: 20,
      timezone: 'UTC'
    },
    loading: false,
    errorMessage: "",
    successMessage: "",
  },
  reducers: {
    messageClear: (state) => {
      state.errorMessage = "";
      state.successMessage = "";
    },
    clearAutoImportState: (state) => {
      state.autoImportEnabled = false;
      state.lastAutoImport = null;
      state.hasVoucherNumber = false;
      state.hasPosConfig = false;
      state.voucherNumber = null;
      state.sellerName = null;
      state.cronSchedule = {
        hour: 12,
        minute: 20,
        timezone: 'UTC'
      };
      state.errorMessage = "";
      state.successMessage = "";
    }
  },
  extraReducers: (builder) => {
    builder
      // Toggle auto-import
      .addCase(toggle_auto_import.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(toggle_auto_import.fulfilled, (state, action) => {
        state.loading = false;
        state.autoImportEnabled = action.payload.autoImportEnabled;
        state.lastAutoImport = action.payload.lastAutoImport;
        state.successMessage = action.payload.message;
        state.errorMessage = "";
      })
      .addCase(toggle_auto_import.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || "Failed to toggle auto-import";
        state.successMessage = "";
      })
      // Get auto-import status
      .addCase(get_auto_import_status.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
      })
      .addCase(get_auto_import_status.fulfilled, (state, action) => {
        state.loading = false;
        state.autoImportEnabled = action.payload.autoImportEnabled;
        state.lastAutoImport = action.payload.lastAutoImport;
        state.hasVoucherNumber = action.payload.hasPosConfig ?? action.payload.hasVoucherNumber;
        state.hasPosConfig = action.payload.hasPosConfig ?? action.payload.hasVoucherNumber;
        state.voucherNumber = action.payload.voucherNumber;
        state.sellerName = action.payload.sellerName;
        state.cronSchedule = action.payload.cronSchedule || {
          hour: 12,
          minute: 20,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        };
        state.errorMessage = "";
      })
      .addCase(get_auto_import_status.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || "Failed to get auto-import status";
      })
      // Update cron schedule
      .addCase(update_cron_schedule.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(update_cron_schedule.fulfilled, (state, action) => {
        state.loading = false;
        state.cronSchedule = action.payload.cronSchedule;
        state.successMessage = action.payload.message;
        state.errorMessage = "";
      })
      .addCase(update_cron_schedule.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || "Failed to update cron schedule";
        state.successMessage = "";
      });
  },
});

export const { messageClear, clearAutoImportState } = autoImportSlice.actions;
export default autoImportSlice.reducer;
